import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import AttributionLockBadge from "../../../components/AttributionLockBadge";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Pill from "../../../components/v3/Pill";
import Btn from "../../../components/v3/Btn";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";
import { useCommissionConfig, formatCommissionPct } from "../../../hooks/useCommissionConfig";
import { useRole } from "../../../hooks/useRole";
import { hasAnyRole } from "../../../utils/complianceRoles";

// Which commission-config family drives each tab's cut chip. The percentage is
// resolved from live config (GET /admin/commission-config) at render time — no
// hardcoded 30 / 15 / 5 here, because a wrong number misleads a contractor
// about their pay. When config is absent the chip is omitted entirely.
const VIEW_TABS = [
  { value: "investors",   label: "Investors",       cutFamily: "bc_direct"   },
  { value: "deals",       label: "Deals",           cutFamily: "deal"        },
  { value: "consultants", label: "Sub-Contractors", cutFamily: "bc_override" },
];
// A consultant may read the Investors view only: the API answers 403
// CONSULTANT_SCOPE_INVESTORS_ONLY for the other two, so they are not offered.
const CONSULTANT_TABS = ["investors"];

const SOURCE_TONE = {
  LINK: "blue",
  CODE: "amber",
  MANUAL: "blue",
  LEGACY: "gray",
  ADMIN_OVERRIDE: "amber",
};

function entityDisplayName(row) {
  // Backend hydrates attribution rows with `Entity` (users for INVESTOR/
  // CONSULTANT, campaign_details for DEAL). Fall back to the raw id only if
  // the join failed — avoids the "#2007"-only rendering BDCs flagged.
  const e = row?.Entity;
  if (!e) return null;
  if (row.entity_type === "DEAL") {
    return e.campaign_name || e.corporation_legal_name || null;
  }
  const full = [e.firstname, e.lastname].filter(Boolean).join(" ");
  return full || e.email || null;
}

function BDCNetworkDashboard() {
  const navigate = useNavigate();
  const [view, setView] = useState("investors");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { config: commissionConfig } = useCommissionConfig();
  // Actual role membership, not hasRole(): hasRole() also answers true for a
  // legacy Super Admin (wildcard), who may read every view.
  const { roles } = useRole();
  const isConsultant = hasAnyRole(roles, "CONSULTANT");
  const tabs = isConsultant ? VIEW_TABS.filter((t) => CONSULTANT_TABS.includes(t.value)) : VIEW_TABS;

  useEffect(() => {
    let live = true;
    setLoading(true);
    bdcApi
      .listNetwork({ view, page, limit })
      .then((res) => {
        if (!live) return;
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || Math.max(1, Math.ceil((d.total || 0) / limit)));
        setError(null);
      })
      .catch((err) => {
        if (!live) return;
        // Never leave the previous tab's rows under this tab's label.
        setRows([]);
        setTotal(0);
        setTotalPages(0);
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [view, page]);

  const currentTab = VIEW_TABS.find((t) => t.value === view);
  const subtitle = (
    <>
      <span className="mono">{total}</span>
      <span>{(currentTab?.label || "").toLowerCase()}</span>
      <span>·</span>
      <span className="mono">page {page}</span>
    </>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="My network" subtitle={subtitle} />

      <div className="toolbar">
        <div className="toolbar__pills" role="tablist">
          {tabs.map((t) => {
            const cut = formatCommissionPct(commissionConfig?.[t.cutFamily]?.pct);
            return (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={view === t.value}
                className={`toolbar__pill${view === t.value ? " toolbar__pill--on" : ""}`}
                onClick={() => {
                  if (t.value !== view) {
                    setRows([]);
                    setTotal(0);
                    setError(null);
                  }
                  setView(t.value);
                  setPage(1);
                }}
              >
                {t.label}
                {cut && (
                  <span style={{ marginLeft: 6, fontFamily: "var(--f-mono)", fontSize: "var(--t-xs)", opacity: 0.75 }}>
                    {cut}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <p
        style={{
          margin: "0 0 12px",
          color: "var(--tx-3)",
          fontSize: "var(--t-sm)",
          lineHeight: 1.4,
        }}
      >
        <strong style={{ color: "var(--tx-2)" }}>Attribution status</strong> —{" "}
        <span style={{ color: "#065f46", fontWeight: 600 }}>Secured</span> means
        your commission credit on this entity is locked in (an admin override is
        the only way it can change after this).{" "}
        <span style={{ color: "#4338ca", fontWeight: 600 }}>Open</span> rows are
        still editable until the investor invests or the deal goes live — no
        action needed from you.
      </p>

      {!loading && rows.length === 0 ? (
        <Empty>
          No {(currentTab?.label || "").toLowerCase()} attributed to you yet. Share a referral link or submit a manual claim in Growth Tools.
        </Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{view === "consultants" ? "Sub-contractor name" : view === "deals" ? "Deal name" : "Investor name"}</th>
                  <th>{view === "deals" ? "Deal ID" : view === "consultants" ? "Sub-contractor email" : "Investor email"}</th>
                  {view === "consultants" && <th>Role</th>}
                  <th>Source</th>
                  <th
                    title="Attribution status — Secured means commission credit is locked to you and an admin override is required to change it."
                  >
                    Attribution
                  </th>
                  <th>Created</th>
                  <th aria-label="Open detail"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const detailUrl = `/bdc/network/${r.entity_type?.toLowerCase() || view}/${r.entity_id}`;
                  const onOpen = () => navigate(detailUrl);
                  const consultantName = r.Consultant
                    ? [r.Consultant.firstname, r.Consultant.lastname].filter(Boolean).join(" ") || r.Consultant.email
                    : null;
                  const entityName = view === "consultants" ? consultantName : entityDisplayName(r);
                  const subText = view === "consultants"
                    ? r.Consultant?.email
                    : (r.Entity?.email || `#${r.entity_id}`);
                  const sourceKey = String(r.source || "").toUpperCase();
                  const sourceTone = SOURCE_TONE[sourceKey] || "gray";
                  const ariaLabel = entityName
                    ? `Open detail for ${entityName}`
                    : `Open detail for entity #${r.entity_id}`;
                  return (
                    <tr
                      key={r.id}
                      tabIndex={0}
                      role="button"
                      aria-label={ariaLabel}
                      onClick={onOpen}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onOpen();
                        }
                      }}
                    >
                      <td>{entityName || <span className="tbl-id">#{r.entity_id}</span>}</td>
                      <td className="tbl-id">{subText}</td>
                      {view === "consultants" && (
                        <td className="muted">{r.role_type || "—"}</td>
                      )}
                      <td>
                        <Pill tone={sourceTone}>{r.source}</Pill>
                      </td>
                      <td>
                        {view === "consultants" && r.is_active === false ? (
                          // An ENDED sponsorship (the consultant role was
                          // revoked or the relationship moved): kept as history.
                          <Pill tone="gray" title="This sponsorship has ended (for example, the consultant role was revoked). It is kept here as history.">
                            Ended
                          </Pill>
                        ) : (
                          <AttributionLockBadge
                            lockedAt={r.locked_at}
                            lockedBy={r.unlocked_by_admin_id}
                          />
                        )}
                      </td>
                      <td className="muted">{formatAuditTimestamp(r.createdAt)}</td>
                      <td className="r">
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); onOpen(); }}
                        >
                          Detail <ChevronRight />
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn
            size="sm"
            disabled={page === 1 || loading}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
            Page {page} of {totalPages}
          </span>
          <Btn
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Btn>
        </div>
      )}
    </PageShell>
  );
}

export default BDCNetworkDashboard;
